import { AbstractControl, ValidatorFn } from '@angular/forms';

export interface DynamicFieldOption {
  name: string;
  description: string;
  replaceableName?: string | null;
}

export function customDynamicFieldValidator(
  dynamicFieldsOptions: DynamicFieldOption[] | (() => DynamicFieldOption[]),
): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    let bodyTextIsValid = null;
    const options = typeof dynamicFieldsOptions === 'function' ? dynamicFieldsOptions() : dynamicFieldsOptions;
    if (!options?.length) {
      return bodyTextIsValid;
    }

    const bodyTextValue: string = control.value ?? '';
    const openBracesCount = (bodyTextValue.match(/{/g) || []).length;
    const closeBracesCount = (bodyTextValue.match(/}/g) || []).length;
    const dynamicFieldsOptionsFromValue = bodyTextValue.match(/\{.*?\}/g);
    const namedDynamicFields = options.filter((opt) => !opt.replaceableName);

    const notFoundDynamicFields = dynamicFieldsOptionsFromValue?.filter((dynamicFieldOptionFromValue) => {
      if (dynamicFieldOptionFromValue.startsWith('{shipping_method.pdq_iq+')) {
        return false;
      }
      return !namedDynamicFields.some((dynamicFieldOption) =>
        dynamicFieldOption.name.includes(dynamicFieldOptionFromValue),
      );
    });

    if (bodyTextValue.includes('{') || bodyTextValue.includes('}')) {
      if (openBracesCount !== closeBracesCount) {
        bodyTextIsValid = { body_text_missmatch: 'Number of braces not match dynamic field.' };
      } else if (notFoundDynamicFields?.length) {
        bodyTextIsValid = {
          body_text_missmatch: `Dynamic fields ${notFoundDynamicFields.map((d) => `"${d}"`).join(', ')} not found.`,
        };
      }

      if (!bodyTextIsValid) {
        bodyTextIsValid = validatePDQIQReplaceableDynamicField(bodyTextValue);
      }
    }
    return bodyTextIsValid;
  };
}

// {shipping_method.pdq_iq+X_business_days:shipping_data:Percentile}
// [{shipping_method.pdq_iq+X_business_days:shipping_data:Percentile}|>2, this is fallback]
// [Arrives by {shipping_method.pdq_iq+X_business_days:shipping_data:Percentile}|>2, this is fallback]
export function validatePDQIQReplaceableDynamicField(bodyTextValue: string): { [key: string]: any } | null {
  const plainText = bodyTextValue.replace(/<[^>]+>/g, '');

  const regex =
    /\[[^\]]*\{shipping_method.pdq_iq\+[^}]+:[^}:]+:[^}]+\}[^\]]*\s*\|\s*(?:>|<|&gt;|&lt;)\s*\d+\s*,\s*[^\]]+\]|{shipping_method.pdq_iq\+[^}]+:[^}:]+:[^}]+}/g;

  let matches = plainText.match(regex) || [];
  if (matches.length === 0) {
    const fallbackRegex = /\{shipping_method.pdq_iq\+[^}]+\}/g;
    matches = plainText.match(fallbackRegex) || [];
  }

  for (const match of matches) {
    if (match.startsWith('[')) {
      if (!match.endsWith(']')) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }
      if (!match.includes('|')) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }

      const inner = match.slice(1, -1);
      const pipeIndex = inner.indexOf('|');
      if (pipeIndex === -1) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }

      const leftSide = inner.slice(0, pipeIndex).trim();
      const conditionPart = inner.slice(pipeIndex + 1).trim();

      if (!conditionPart) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }

      const innerFieldMatch = leftSide.match(/{shipping_method.pdq_iq\+[^}]+:[^}:]+:[^}]+}/);
      if (!innerFieldMatch) {
        return { body_text_missmatch: `There are variants that are not correct` };
      }

      const simpleError = validateSimplePDQIQReplaceableDynamicField(innerFieldMatch[0]);
      if (simpleError) return simpleError;

      let normalizedConditionPart = conditionPart.trim();
      normalizedConditionPart = normalizedConditionPart.replace(/&gt;/g, '>').replace(/&lt;/g, '<');

      const conditionRegex = /^(>|<|&gt;|&lt;)\s*(\d+)\s*,\s*(.+)$/;
      const conditionMatch = normalizedConditionPart.match(conditionRegex);
      if (!conditionMatch) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }

      const [, , , fallback] = conditionMatch;

      const cleanedFallback = fallback.replace(/<[^>]*>/g, '').trim();
      if (!cleanedFallback) {
        return { body_text_missmatch: 'There are variants that are empty' };
      }
    } else {
      const simpleError = validateSimplePDQIQReplaceableDynamicField(match);
      if (simpleError) return simpleError;

      const startIndex = plainText.indexOf(match);
      const endIndex = startIndex + match.length;
      const before = plainText[startIndex - 1] || '';
      const after = plainText[endIndex] || '';

      if (before === '[' || after === ']') {
        return { body_text_missmatch: 'There are variants that are not correct' };
      }
      if (after === '|') {
        return { body_text_missmatch: 'There are variants that are not correct' };
      }
    }
  }

  return null;
}

/**
 * Validate dynamic field with replaceableName syntax like:
 *   {shipping_method.pdq_iq+X_business_days:shipping_data:Percentile}
 *
 * Rules:
 *  - X must be a number (not literal "X")
 *  - "shipping_data" placeholder must be replaced with real value
 *  - "Percentile" placeholder must be replaced with real value
 */
export function validateSimplePDQIQReplaceableDynamicField(field: string): { [key: string]: any } | null {
  const inside = field.slice(1, -1);

  const parts = inside.split(':');
  if (parts.length !== 3) {
    return { body_text_missmatch: 'There are variants that are empty' };
  }

  const [xPartRaw, shippingValue, percentileValue] = parts;
  const xMatch = xPartRaw.match(/^shipping_method.pdq_iq\+([^_]+)_business_days$/);

  if (!xMatch) {
    return { body_text_missmatch: 'There are variants that are empty' };
  }

  const xValue = xMatch[1];

  if (xValue === 'X' || !/^\d+$/.test(xValue)) {
    return { body_text_missmatch: 'There are variants that are empty' };
  }

  if (!shippingValue || shippingValue?.trim()?.length === 0 || shippingValue === 'shipping_data') {
    return { body_text_missmatch: 'There are variants that are empty' };
  }

  if (!percentileValue || percentileValue?.trim()?.length === 0 || percentileValue === 'Percentile') {
    return { body_text_missmatch: 'There are variants that are empty' };
  }

  return null;
}
